import { getLanguageCodeByLocale } from 'common/enums/LanguageCodeEnum'
import StyleWrapper from 'publisher/components/core/StyleWrapper'
import FieldErrors from 'publisher/components/FieldErrors'
import {
  managementSelectors,
  typedPageSelectors,
  useManagement,
  useOptIn,
  usePage,
} from 'publisher/store'
import { getCustomerCountry } from 'publisher/store/optIn/optInSelectors'
import React from 'react'
import { useCalculateFormFieldStyles } from 'src/publisher/shared/hooks/use-calculate-form-fields-styles'
import { useVisitorCountryFallback } from 'src/publisher/shared/hooks/use-visitor-country-fallback'
import { FieldIconNew } from 'src/shared/components/field-icon-new'
import { FieldWrapper } from 'src/shared/components/field-wrapper'
import { PhoneInputProps } from 'src/shared/components/phone-input'
import { PhoneNumberFieldNew } from 'src/shared/components/phone-number-field-new'
import { ContactUsFieldEntityInterface } from 'src/shared/entities/contact-us-field/contact-us-field-entity.schema'
import { FieldEntityInterface } from 'src/shared/entities/field/field-entity.schema'
import { CommonFieldSubEntityInterface } from 'src/shared/schemas/field/common-field-sub-entity.schema'

export interface CommonPublisherPhoneNumberFieldEntityOrSubEntityProps {
  entityOrSubEntity:
    | FieldEntityInterface
    | ContactUsFieldEntityInterface
    | CommonFieldSubEntityInterface
  value: string | undefined
  onChange: (value: string) => void
  errors: string[]
  isHidden?: boolean
}

export const CommonPublisherPhoneNumberFieldEntityOrSubEntity = ({
  entityOrSubEntity,
  value,
  onChange,
  errors,
  isHidden,
}: CommonPublisherPhoneNumberFieldEntityOrSubEntityProps) => {
  const locale = usePage(typedPageSelectors.getLocale)

  const customerCountry = useOptIn(getCustomerCountry) || undefined
  const preloadedCountry = useManagement(managementSelectors.getVisitorCountry)
  const visitorCountry = useVisitorCountryFallback(
    !customerCountry && !preloadedCountry,
  )
  const initialCountry = (
    customerCountry ||
    preloadedCountry ||
    visitorCountry
  )?.toLowerCase()
  const isWaitingForVisitorCountry =
    !customerCountry && !preloadedCountry && visitorCountry === undefined

  const handlePhoneNumber: PhoneInputProps['onChange'] = ({
    unformatedPhone,
  }) => {
    onChange(unformatedPhone)
  }

  const iconColor = entityOrSubEntity.color || 'initial'
  const mobileIconColor = entityOrSubEntity.mobileColor || iconColor

  const {
    calculatedBorder,
    calculatedPadding,
    calculatedFontSize,
    calculatedLineHeight,
    calculatedTextAlign,
    calculatedFontFamily,
    calculatedColor,
    calculatedBackgroundColor,
  } = useCalculateFormFieldStyles({
    border: entityOrSubEntity.border,
    mobileBorder: entityOrSubEntity.mobileBorder,
    padding: entityOrSubEntity.padding,
    mobilePadding: entityOrSubEntity.mobilePadding,
    fontSize: entityOrSubEntity.fontSize,
    mobileFontSize: entityOrSubEntity.mobileFontSize,
    lineHeight: entityOrSubEntity.lineHeight,
    mobileLineHeight: entityOrSubEntity.mobileLineHeight,
    textAlign: entityOrSubEntity.textAlign,
    mobileTextAlign: entityOrSubEntity.mobileTextAlign,
    color: entityOrSubEntity.color,
    mobileColor: entityOrSubEntity.mobileColor,
    backgroundColor: entityOrSubEntity.backgroundColor,
    mobileBackgroundColor: entityOrSubEntity.mobileBackgroundColor,
  })

  return (
    <StyleWrapper
      id={entityOrSubEntity.htmlAttrId}
      margin={entityOrSubEntity.margin}
      mobileMargin={entityOrSubEntity.mobileMargin}
      appearance={entityOrSubEntity.appearance}
    >
      {typeof window !== 'undefined' && !isHidden && (
        <FieldWrapper>
          {entityOrSubEntity.iconClassName && (
            <FieldIconNew
              color={iconColor}
              mobileColor={mobileIconColor}
              fontSize={calculatedFontSize.desktop}
              mobileFontSize={calculatedFontSize.mobile}
            />
          )}
          <PhoneNumberFieldNew
            key={initialCountry || 'default-country'}
            initialCountry={initialCountry}
            value={value || ''}
            onChange={handlePhoneNumber}
            isLoading={isWaitingForVisitorCountry}
            hasIcon={!!entityOrSubEntity.iconClassName}
            hasErrors={!!errors.length}
            textAlign={calculatedTextAlign.desktop}
            mobileTextAlign={calculatedTextAlign.mobile}
            padding={calculatedPadding.desktop}
            mobilePadding={calculatedPadding.mobile}
            border={calculatedBorder.desktop}
            mobileBorder={calculatedBorder.mobile}
            backgroundColor={calculatedBackgroundColor.desktop}
            mobileBackgroundColor={calculatedBackgroundColor.mobile}
            color={calculatedColor.desktop}
            mobileColor={calculatedColor.mobile}
            placeholder={entityOrSubEntity.placeholder}
            placeholderColor={entityOrSubEntity.placeholderColor}
            mobilePlaceholderColor={entityOrSubEntity.mobilePlaceholderColor}
            fontFamily={calculatedFontFamily.desktop}
            mobileFontFamily={calculatedFontFamily.mobile}
            fontSize={calculatedFontSize.desktop}
            mobileFontSize={calculatedFontSize.mobile}
            lineHeight={calculatedLineHeight.desktop}
            mobileLineHeight={calculatedLineHeight.mobile}
            boxShadow={entityOrSubEntity.boxShadow}
            mobileBoxShadow={entityOrSubEntity.mobileBoxShadow}
            countrySelectorStyleProps={{
              dropdownStyleProps: {
                style: { fontSize: entityOrSubEntity.fontSize },
                searchInputStyle: { fontSize: entityOrSubEntity.fontSize },
              },
            }}
            countrySelectorProps={{
              language: getLanguageCodeByLocale(locale),
            }}
            wrapperProps={{
              id: entityOrSubEntity.htmlAttrId,
            }}
          />
        </FieldWrapper>
      )}
      <FieldErrors errors={errors} />
    </StyleWrapper>
  )
}
